/**
 * A function that checks if a value is unique within an array. It can be used as a predicate
 * for `Array.prototype.filter` to filter out duplicate values, retaining only the first occurrence.
 *
 * @example
 * const data = [1, 2, 2, 3, 4, 4, 5];
 * const uniqueData = data.filter(unique);
 * // uniqueData is now: [1, 2, 3, 4, 5]
 */
export const unique = <T>(value: T, index: number, array: readonly T[]) => array.indexOf(value) === index;

/**
 * Creates a function that filters an array of objects by ensuring each object is unique
 * based on a specific key. The resulting function can be used as a predicate for `Array.prototype.filter`.
 *
 * @example
 * const data = [
 *   { id: 1, name: 'Alice' },
 *   { id: 2, name: 'Bob' },
 *   { id: 3, name: 'Alice' }
 * ];
 *
 * const uniqueData = data.filter(uniqueBy('name'));
 * // uniqueData is now:
 * // [
 * //   { id: 1, name: 'Alice' },
 * //   { id: 2, name: 'Bob' },
 * // ]
 */
export const uniqueBy =
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  <TObj extends object, TKey extends keyof TObj>(key: TKey) =>
    (value: TObj, index: number, array: TObj[]) =>
      array.map((element) => element[key]).indexOf(value[key]) === index;
